import type { SubscribedPowerId } from "@/types/common/ids";
import type { EnumKeys } from "@/types/utils.ts";

import { cloneDeep } from "es-toolkit";
import { z } from "zod";

import { brandNumber } from "@/lib/branded";
import { branded } from "@/lib/helpers/branded-helper";

export enum ESubscriberPowerType {
  POWER_TYPE_3KW = "POWER_TYPE_3KW",
  POWER_TYPE_6KW = "POWER_TYPE_6KW",
  POWER_TYPE_9KW = "POWER_TYPE_9KW",
  POWER_TYPE_12KW = "POWER_TYPE_12KW",
  POWER_TYPE_15KW = "POWER_TYPE_15KW",
  POWER_TYPE_18KW = "POWER_TYPE_18KW",
  POWER_TYPE_24KW = "POWER_TYPE_24KW",
  POWER_TYPE_30KW = "POWER_TYPE_30KW",
  POWER_TYPE_36KW = "POWER_TYPE_36KW",
  POWER_TYPE_SUP_THAN_250KW = "POWER_TYPE_SUP_THAN_250KW",
  POWER_TYPE_SUP_THAN_36KW = "POWER_TYPE_SUP_THAN_36KW",
  UNKNOWN = "UNKNOWN",
}

export interface ISubscriberPowerType {
  readonly id: SubscribedPowerId;
  readonly description: string;
  readonly power: number | null;
  readonly refCountryId: number | null;
}

export const subscriberPowerTypeSchema = z.object({
  id: branded.subscribedPowerId,
  description: z.string(),
  power: z.number().nullable(),
  refCountryId: z.number().nullable(),
});

export const SubscriberPowerTypes: {
  [K in EnumKeys<typeof ESubscriberPowerType>]: ISubscriberPowerType;
} = {
  [ESubscriberPowerType.UNKNOWN]: {
    id: brandNumber(10),
    description: "Inconnu",
    power: null,
    refCountryId: 1,
  },
  [ESubscriberPowerType.POWER_TYPE_3KW]: {
    id: brandNumber(1),
    description: "3kW",
    power: 3,
    refCountryId: 1,
  },
  [ESubscriberPowerType.POWER_TYPE_6KW]: {
    id: brandNumber(2),
    description: "6kW",
    power: 6,
    refCountryId: 1,
  },
  [ESubscriberPowerType.POWER_TYPE_9KW]: {
    id: brandNumber(3),
    description: "9kW",
    power: 9,
    refCountryId: 1,
  },
  [ESubscriberPowerType.POWER_TYPE_12KW]: {
    id: brandNumber(4),
    description: "12kW",
    power: 12,
    refCountryId: 1,
  },
  [ESubscriberPowerType.POWER_TYPE_15KW]: {
    id: brandNumber(5),
    description: "15kW",
    power: 15,
    refCountryId: 1,
  },
  [ESubscriberPowerType.POWER_TYPE_18KW]: {
    id: brandNumber(6),
    description: "18kW",
    power: 18,
    refCountryId: 1,
  },
  [ESubscriberPowerType.POWER_TYPE_24KW]: {
    id: brandNumber(7),
    description: "24kW",
    power: 24,
    refCountryId: 1,
  },
  [ESubscriberPowerType.POWER_TYPE_30KW]: {
    id: brandNumber(8),
    description: "30kW",
    power: 30,
    refCountryId: 1,
  },
  [ESubscriberPowerType.POWER_TYPE_36KW]: {
    id: brandNumber(9),
    description: "36kW",
    power: 36,
    refCountryId: 1,
  },
  [ESubscriberPowerType.POWER_TYPE_SUP_THAN_36KW]: {
    id: brandNumber(12),
    description: "> 36kW",
    power: null,
    refCountryId: 1,
  },
  [ESubscriberPowerType.POWER_TYPE_SUP_THAN_250KW]: {
    id: brandNumber(11),
    description: "> 250kW",
    power: null,
    refCountryId: 1,
  },
};

export function getSubscriberPowerTypeById(id?: number | null): ISubscriberPowerType | undefined {
  if (id === undefined || id === null) {
    return undefined;
  }

  return Object.values(SubscriberPowerTypes).find((photoType) => photoType.id === id);
}
export function getAllSubscriberPowerTypes(): Array<ISubscriberPowerType> {
  return cloneDeep(Object.values(SubscriberPowerTypes));
}
